import { useState } from 'react'
import { useRegisterClientMutation } from '@/entities/client'
import { errorMessage } from '@/shared/api'
import { Button } from '@/shared/ui/button'
import { Dialog } from '@/shared/ui/dialog'
import { Field, Input, Textarea } from '@/shared/ui/field'
import { toast } from '@/shared/ui/toast'

interface ClientCreateDialogProps {
  open: boolean
  /** Набранное в поиске: контакт заводят как раз тогда, когда его там не нашли. */
  prefill?: string
  onClose: () => void
}

const EMPTY = { name: '', phone: '', note: '' }

/** Цифры со скобками и плюсом — это телефон, всё остальное — имя. */
function draftFrom(query: string) {
  const value = query.trim()
  if (!value) return EMPTY
  return /^[\d\s()+-]{5,}$/.test(value) ? { ...EMPTY, phone: value } : { ...EMPTY, name: value }
}

/**
 * Заведение контакта вручную — для тех, кто пришёл не по ссылке, а по звонку
 * или через знакомых. Дубля из повторного заведения не выходит: совпадение по
 * паре имя+телефон ищет бэкенд и возвращает найденный контакт.
 */
export function ClientCreateDialog({ open, prefill = '', onClose }: ClientCreateDialogProps) {
  const register = useRegisterClientMutation()
  const [form, setForm] = useState(EMPTY)
  const [key, setKey] = useState<string | null>(null)

  // Диалог живёт рядом со списком: при открытии поля перезаполняются из поиска,
  // а не тянут прежний ввод.
  if (open && key !== prefill) {
    setKey(prefill)
    setForm(draftFrom(prefill))
  }
  if (!open && key !== null) setKey(null)

  const close = () => {
    register.reset()
    onClose()
  }

  const submit = (event: React.FormEvent) => {
    event.preventDefault()
    register.mutate(
      { name: form.name.trim(), phone: form.phone.trim(), note: form.note.trim() },
      {
        onSuccess: (result) => {
          close()
          toast.success(result.matched ? 'Такой контакт уже есть' : 'Клиент заведён')
        },
      },
    )
  }

  const filled = form.name.trim().length > 0 && form.phone.trim().length > 0

  return (
    <Dialog
      open={open}
      onClose={close}
      title="Новый клиент"
      footer={
        <>
          <Button variant="ghost" onClick={close}>
            Отмена
          </Button>
          <Button type="submit" form="client-create-form" disabled={!filled || register.isPending}>
            Завести
          </Button>
        </>
      }
    >
      <form id="client-create-form" onSubmit={submit} className="flex flex-col gap-4">
        <Field label="Имя">
          {(props) => (
            <Input
              {...props}
              value={form.name}
              autoFocus
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
          )}
        </Field>
        <Field label="Телефон" hint="Один номер может быть у нескольких человек — это нормально">
          {(props) => (
            <Input
              {...props}
              type="tel"
              inputMode="tel"
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
            />
          )}
        </Field>
        <Field label="Заметка">
          {(props) => (
            <Textarea
              {...props}
              value={form.note}
              onChange={(e) => setForm({ ...form, note: e.target.value })}
            />
          )}
        </Field>

        <p className="text-xs text-text-tertiary">
          Согласие на обработку данных записывается отдельно — когда человек читает его на форме
          записи или когда его записывают на выход со слов.
        </p>

        {register.isError && (
          <p role="alert" className="text-sm text-accent">
            {errorMessage(register.error)}
          </p>
        )}
      </form>
    </Dialog>
  )
}
